import { createContext, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import once from 'lodash/once';

import SoyuzAnalytics from '@spotahome/soyuz-analytics';

import { scrollVerticallyTo } from '../utils/scroller';
import { useIsElementStuck } from '../utils/hooks/useIsElementStuck';

// NOTE: Multibrowser support for scrolling pixel float value calculation roundings
const SAFE_SUBPIXEL_OFFSET = 1;

const ListingNavigationContext = createContext({
  sectionInView: null,
  handleInView: () => {},
  setHeight: () => {},
  scrollToSection: () => {},
  height: 0,
  navigationRef: { current: null },
  isStuck: false,
  isExperimentVariant: false
});

const trackSectionSeen = once(section => {
  SoyuzAnalytics.sendGA4Event('listing-section-seen', {
    section
  });
});

const trackNavigationStuck = once(() => {
  SoyuzAnalytics.sendGA4Event('listing-navigation-stuck', {});
});

export const ListingNavigationProvider = ({ children }) => {
  const [sectionInView, setSectionInView] = useState(null);
  const [height, setHeight] = useState(0);
  const { elem: navigationRef, isStuck } = useIsElementStuck();

  const handleInView = section => {
    if (section !== sectionInView) {
      setSectionInView(section);
    }
    trackSectionSeen(section);
  };

  const scrollToSection = section => {
    const offset = -height + SAFE_SUBPIXEL_OFFSET;
    scrollVerticallyTo({
      attribute: {
        name: 'sticky-in-navigation',
        value: section
      },
      offset
    });
  };

  useEffect(() => {
    if (isStuck) {
      trackNavigationStuck();
    }
  }, [isStuck]);

  return (
    <ListingNavigationContext.Provider
      value={{
        sectionInView,
        handleInView,
        setHeight,
        scrollToSection,
        height,
        navigationRef,
        isStuck
      }}
    >
      {children}
    </ListingNavigationContext.Provider>
  );
};

ListingNavigationProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]).isRequired
};

export const useListingNavigation = () => useContext(ListingNavigationContext);

export default {
  ListingNavigationProvider,
  useListingNavigation
};
